import React, { useState, useEffect, useRef, useCallback } from "react";
import "../housestyle.css";
import "./Giveaways.css";
import api from "../API/Api";
import countdown_tick from "./countdown_tick.mp3";
import ping_tick from "./ping.mp3";
import winner_sfx from "./winner.mp3";
// Note: countdown_tick, ping_tick, winner_sfx are used via module-level Audio objects below

// Create at module scope so the prop named "winner" inside SingleSpin doesn't shadow them
const pingAudio = new Audio(ping_tick);
pingAudio.volume = 0.3;
const winnerAudio = new Audio(winner_sfx);
const countdownAudio = new Audio(countdown_tick);

// Mute state persisted across sessions — off by default
let sfxMuted = localStorage.getItem("gw_sfx_muted") !== "0";
function setGlobalMute(muted) {
  sfxMuted = muted;
  try { localStorage.setItem("gw_sfx_muted", muted ? "1" : "0"); } catch { }
}
function playAudio(audio) {
  if (sfxMuted) return;
  audio.currentTime = 0;
  audio.play().catch(() => { });
}

// Browsers block audio until the user interacts. Unlock all audio on first interaction.
function unlockAudio() {
  if (sfxMuted) return;
  [pingAudio, winnerAudio, countdownAudio].forEach((a) => {
    const vol = a.volume;
    a.volume = 0;
    a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = vol; }).catch(() => { });
  });
  document.removeEventListener("pointerdown", unlockAudio);
}
document.addEventListener("pointerdown", unlockAudio);

// Works for both discord and playtime entries/winners
function entryName(e) {
  return e.player_name ?? e.discord_tag ?? "";
}
function entryTickets(e) {
  return e.tickets ?? e.invites ?? 1;
}

const video =
  "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4";

function spinKey(g) {
  return `gw_watched_${g.id}_${g.end_date}`;
}
function hasWatchedSpin(g) {
  try {
    return localStorage.getItem(spinKey(g)) === "1";
  } catch {
    return false;
  }
}
function markSpinWatched(g) {
  try {
    localStorage.setItem(spinKey(g), "1");
  } catch { }
}

function useCountdown(endDate) {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!endDate) return;
    const tick = () => {
      const diff = new Date(endDate) - Date.now();
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [endDate]);
  return timeLeft;
}

function PreSpinCountdown({ onDone }) {
  const [count, setCount] = useState(10);
  useEffect(() => {
    if (count <= 0) {
      onDone();
      return;
    }
    playAudio(countdownAudio);
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, onDone]);

  return (
    <div className="prespin-wrap">
      <div className="prespin-label">Spin starts in</div>
      <div className="prespin-count" key={count}>
        {count}
      </div>
      <div className="prespin-sub">Get ready...</div>
    </div>
  );
}

function SingleSpin({ winner, allDiscordTags, onDone, prizeLabel }) {
  const [phase, setPhase] = useState("spinning");
  const [displayed, setDisplayed] = useState([]);
  const timerRef = useRef(null);
  const speedRef = useRef(60);
  const tickCountRef = useRef(0);

  const winnerName = entryName(winner);

  useEffect(() => {
    const pool = allDiscordTags.length > 0 ? allDiscordTags : [winnerName];
    const others = pool.filter((n) => n !== winnerName);
    const filler = others.length > 0 ? others : pool;
    const pick = (exclude) => {
      const opts = filler.filter((n) => n !== exclude);
      const src = opts.length > 0 ? opts : filler;
      return src[Math.floor(Math.random() * src.length)];
    };

    const endA = pick(null);
    const endB = pick(endA);
    const endC = pick(winnerName);
    const endD = pick(endC);
    const fixedTail = [endA, endB, winnerName, endC, endD];

    const tape = [];
    for (let i = 0; i < 55; i++)
      tape.push(filler[Math.floor(Math.random() * filler.length)]);
    tape.push(...fixedTail);

    const STOP_AT = 55;
    const FAST_UNTIL = 45;
    let currentIdx = 0;

    const tick = () => {
      setDisplayed(tape.slice(currentIdx, currentIdx + 5));
      if (currentIdx >= STOP_AT) {
        clearInterval(timerRef.current);
        setPhase("landed");
        setTimeout(() => {
          setPhase("revealed");
          playAudio(winnerAudio);
          setTimeout(onDone, 3500);
        }, 2000);
        return;
      }
      // Play ping every 4 ticks when fast, every tick when slowing down
      tickCountRef.current += 1;
      const pingEvery = currentIdx >= FAST_UNTIL ? 1 : 4;
      if (tickCountRef.current % pingEvery === 0) {
        playAudio(pingAudio);
      }
      currentIdx++;
      if (currentIdx >= FAST_UNTIL) {
        clearInterval(timerRef.current);
        speedRef.current = Math.min(speedRef.current + 40, 700);
        timerRef.current = setInterval(tick, speedRef.current);
      }
    };

    timerRef.current = setInterval(tick, 55);
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line

  return (
    <div className="spin-wrap">
      <div className="spin-prize-badge">{prizeLabel}</div>
      <div className="spin-title">
        {phase === "spinning"
          ? "Spinning..."
          : phase === "landed"
            ? "We have a winner!"
            : "Winner!"}
      </div>
      <div className={`spin-reel ${phase === "spinning" ? "spinning" : ""}`}>
        {phase === "revealed" ? (
          <div className="spin-winner-card">
            <img
              src={`https://mc-heads.net/avatar/${winnerName}/96`}
              alt={winnerName}
              className="spin-winner-avatar"
            />
            <div className="spin-winner-name">{winnerName}</div>
            <div className="spin-winner-prize">{winner.prize}</div>
          </div>
        ) : (
          displayed.map((name, i) => (
            <div
              key={i}
              className={`spin-item ${i === 2 ? "spin-item-active" + (phase === "landed" ? " spin-item-landed" : "") : ""}`}
            >
              <img src={`https://mc-heads.net/avatar/${name}/32`} alt={name} />
              <span>{name}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SpinSequence({ winners, allDiscordTags, giveaway }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [done, setDone] = useState(false);

  const handleDone = useCallback(() => {
    if (currentIdx + 1 < winners.length) {
      setCurrentIdx((i) => i + 1);
    } else {
      markSpinWatched(giveaway);
      setDone(true);
    }
  }, [currentIdx, winners.length, giveaway]);

  const placeLabels = ["🥇 1st Place", "🥈 2nd Place", "🥉 3rd Place"];

  if (done) {
    return (
      <div className="spin-wrap">
        <div className="spin-title">All Winners!</div>
        <div className="spin-all-winners">
          {winners.map((w, i) => (
            <div key={i} className="spin-winner-card spin-winner-card--row">
              <img
                src={`https://mc-heads.net/avatar/${entryName(w)}/64`}
                alt={entryName(w)}
                className="spin-winner-avatar spin-winner-avatar--sm"
              />
              <div>
                <div className="spin-winner-name">{entryName(w)}</div>
                <div className="spin-winner-prize">{w.prize}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <SingleSpin
      key={currentIdx}
      winner={winners[currentIdx]}
      allDiscordTags={allDiscordTags}
      onDone={handleDone}
      prizeLabel={placeLabels[currentIdx] ?? `#${currentIdx + 1}`}
    />
  );
}

function SpinSection({ winners, allDiscordTags, giveaway }) {
  const [preSpinDone, setPreSpinDone] = useState(false);
  const [muted, setMuted] = useState(sfxMuted);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setGlobalMute(next);
  };

  return (
    <div className="spin-section-wrap">
      <button className="gw-audio-btn" onClick={toggleMute}>
        Audio: {muted ? "Off" : "On"}
      </button>
      {!preSpinDone
        ? <PreSpinCountdown onDone={() => setPreSpinDone(true)} />
        : <SpinSequence winners={winners} allDiscordTags={allDiscordTags} giveaway={giveaway} />
      }
    </div>
  );
}

function WinnersSummary({ winners }) {
  return (
    <div className="spin-wrap">
      <div className="spin-title">Winners</div>
      <div className="spin-all-winners">
        {winners.map((w, i) => (
          <div key={i} className="spin-winner-card spin-winner-card--row">
            <img
              src={`https://mc-heads.net/avatar/${entryName(w)}/64`}
              alt={entryName(w)}
              className="spin-winner-avatar spin-winner-avatar--sm"
            />
            <div>
              <div className="spin-winner-name">{entryName(w)}</div>
              <div className="spin-winner-prize">{w.prize}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PLACE_STYLE = {
  1: { label: "🥇 1st", color: "#f0ab20" },
  2: { label: "🥈 2nd", color: "#b0b8c8" },
  3: { label: "🥉 3rd", color: "#c87840" },
};

function EntriesModal({ title, entries, isPlaytime, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalTickets = entries.reduce((s, e) => s + Math.max(1, entryTickets(e)), 0);

  return (
    <div
      className="gw-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="gw-modal-box">
        <div className="gw-modal-header">
          <div>
            <div className="gw-modal-title">{title}</div>
            <div className="gw-modal-sub">
              {entries.length} participants · {totalTickets} total tickets
            </div>
          </div>
          <button className="gw-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="gw-modal-table-head">
          <span>Player</span>
          <span>Past wins</span>
          <span>{isPlaytime ? "Minutes" : "Invites"}</span>
          <span>Win chance</span>
        </div>

        <div className="gw-modal-rows">
          {entries.length === 0 && (
            <div className="gw-modal-empty">No entries yet.</div>
          )}
          {entries.map((player, i) => {
            const tickets = Math.max(1, entryTickets(player));
            const chance =
              totalTickets > 0
                ? ((tickets / totalTickets) * 100).toFixed(2)
                : "0.00";
            const place = PLACE_STYLE[player.place];
            const name = entryName(player);
            return (
              <div
                key={i}
                className={`gw-modal-row${player.is_winner ? " gw-modal-row--winner" : ""}`}
                style={place ? { borderLeft: `3px solid ${place.color}` } : {}}
              >
                <div className="gw-modal-player">
                  <img
                    src={`https://mc-heads.net/avatar/${name}/32`}
                    alt={name}
                    className="gw-modal-avatar"
                  />
                  <div className="gw-modal-name-wrap">
                    <span
                      className="gw-modal-name"
                      style={place ? { color: place.color } : {}}
                    >
                      {name}
                    </span>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {place && (
                        <span
                          className="gw-modal-place-badge"
                          style={{ color: place.color }}
                        >
                          {place.label}
                        </span>
                      )}
                      {player.double_chance && (
                        <span className="gw-modal-egg-badge">🥚 2× Chance</span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="gw-modal-wins">
                  {player.won_giveaways > 0 ? (
                    <span className="gw-modal-wins-badge">
                      {player.won_giveaways} Wins
                    </span>
                  ) : (
                    <span className="gw-modal-wins-none">0</span>
                  )}
                </span>
                <span className="gw-modal-invites">
                  {isPlaytime ? (player.playtime_minutes ?? tickets) : entryTickets(player)}
                </span>
                <span className="gw-modal-chance">{chance}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HowToEnter({ giveaway }) {
  const minHours = giveaway.min_playtime_seconds
    ? Math.round(giveaway.min_playtime_seconds / 3600)
    : null;

  if (giveaway.type === "playtime") {
    return (
      <div className="gw-howto">
        <div className="gw-howto-steps">
          <div className="gw-howto-step">
            <div className="gw-howto-num">1</div>
            <span className="gw-howto-title">Join the server</span>
            <span className="gw-howto-desc">— connect to <strong>play.simplemcnetwork.net</strong></span>
          </div>
          <div className="gw-howto-step">
            <div className="gw-howto-num">2</div>
            <span className="gw-howto-title">Play</span>
            {minHours !== null && (
              <span className="gw-howto-desc">— reach at least <strong>{Math.round(giveaway.min_playtime_seconds / 60)} min</strong> of playtime to qualify</span>
            )}
          </div>
          <div className="gw-howto-step">
            <div className="gw-howto-num">3</div>
            <span className="gw-howto-title">Equal chance for everyone</span>
            <span className="gw-howto-desc">— every qualifying player gets 1 ticket</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gw-howto">
      <div className="gw-howto-steps">
        <div className="gw-howto-step">
          <div className="gw-howto-num">1</div>
          <a className="gw-howto-title gw-howto-link" href="https://discord.com/invite/simplemc" target="_blank" rel="noreferrer">Join Our Discord</a>
        </div>
        <div className="gw-howto-step">
          <div className="gw-howto-num">2</div>
          <span className="gw-howto-title">Invite friends</span>
          <span className="gw-howto-desc">- Invites friends with your own link by using <code>/myinvitelink</code></span>
        </div>
        <div className="gw-howto-step">
          <div className="gw-howto-num">3</div>
          <span className="gw-howto-title">Check spots</span>
          <span className="gw-howto-desc">- run <code>/invites</code> in <strong>#invites</strong></span>
        </div>
      </div>
    </div>
  );
}

function CountdownBlock({ label, value }) {
  return (
    <div className="gw-cd-block">
      <div className="gw-cd-num">{String(value).padStart(2, "0")}</div>
      <div className="gw-cd-label">{label}</div>
    </div>
  );
}

function PastCard({ giveaway, onViewEntries }) {
  return (
    <div className="gw-past-card card">
      <div className="gw-past-title">{giveaway.title}</div>
      <div className="gw-past-date">
        {new Date(giveaway.end_date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })}
      </div>
      <div className="gw-past-winners">
        {giveaway.winners.map((w, i) => (
          <div key={i} className="gw-past-winner-row">
            <img
              src={`https://mc-heads.net/avatar/${entryName(w)}/40`}
              alt={entryName(w)}
              className="gw-past-avatar"
            />
            <div>
              <div className="gw-past-winner-name">{entryName(w)}</div>
              <div className="gw-past-winner-prize">{w.prize}</div>
            </div>
          </div>
        ))}
      </div>
      {giveaway.entries?.length > 0 && (
        <button className="gw-view-entries-btn" onClick={onViewEntries}>
          View all entries ({giveaway.entries.length})
        </button>
      )}
    </div>
  );
}

export default function GiveawayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [frozenWinners, setFrozenWinners] = useState(null);
  const [frozenEntries, setFrozenEntries] = useState(null);
  const [spinTriggered, setSpinTriggered] = useState(false);
  const [entriesModal, setEntriesModal] = useState(null);

  useEffect(() => {
    let active = true;
    let pickCalled = false;

    const load = async () => {
      const res = await api("getgiveaway");
      if (!active) return;
      if (!res.isSuccess) {
        setError("Failed to load giveaway data.");
        setLoading(false);
        return;
      }
      const d = res.data;
      setData(d);
      setLoading(false);

      // Pick winners 10s after end_date
      if (d.giveaway?.status === "active" && !pickCalled) {
        const msUntilPick = new Date(d.giveaway.end_date) - Date.now() + 10000;
        setTimeout(
          () => {
            if (!active) return;
            pickCalled = true;
            api("pickwinners");
          },
          Math.max(0, msUntilPick),
        );
      }

      // Trigger spin for anyone who hasn't watched it yet
      if (
        d.giveaway?.status === "finished" &&
        d.winners?.length > 0 &&
        !spinTriggered &&
        !hasWatchedSpin(d.giveaway)
      ) {
        setFrozenWinners(d.winners);
        setFrozenEntries(d.entries ?? []);
        setSpinTriggered(true);
      }
    };

    load();
    const iv = setInterval(load, 10000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [spinTriggered]);

  const timeLeft = useCountdown(data?.giveaway?.end_date);
  const isFinished = data?.giveaway?.status === "finished";
  const isActive = data?.giveaway?.status === "active";
  const isPlaytime = data?.giveaway?.type === "playtime";
  const awaitingWinners = isActive && !timeLeft;
  const showSpin = spinTriggered && frozenWinners?.length > 0;
  const showSummary = !showSpin && isFinished && data?.winners?.length > 0;

  if (loading)
    return (
      <div
        style={{
          padding: "4rem",
          textAlign: "center",
          color: "var(--text-muted)",
        }}
      >
        Loading giveaway…
      </div>
    );
  if (error)
    return (
      <div
        style={{
          padding: "4rem",
          textAlign: "center",
          color: "var(--text-muted)",
        }}
      >
        {error}
      </div>
    );

  const { giveaway, past } = data;
  const allDiscordTags =
    frozenEntries?.map((e) => entryName(e)).filter(Boolean) ?? [];

  return (
    <>
      <div className="gw-hero">
        <video
          className="gw-hero-video"
          src={video}
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="gw-hero-overlay" />
        <div className="gw-hero-content container">
          <div className="gw-hero-eyebrow" style={{ color: "var(--text)" }}>
            Giveaways
          </div>
          <h1 className="gw-hero-title">
            Give<em>aways</em>
          </h1>
          <p className="gw-hero-sub">
            Win exclusive prizes | invite friends and earn spots on the wheel.
          </p>
          <div className="gw-hero-chips">
            {isActive && (data.entries?.length ?? 0) > 0 && (
              <span className="gw-hero-chip">
                {data.entries.length} entries
              </span>
            )}
            {isActive && timeLeft && (
              <span className="gw-hero-chip gw-hero-chip--live">Active</span>
            )}
            {isFinished && <span className="gw-hero-chip">Giveaway Ended</span>}
          </div>
        </div>
      </div>

      <div className="container section">
        <div className="section-label">
          <span className="sl-text">
            {isFinished ? "Latest Giveaway" : "Active Giveaway"}
          </span>
        </div>

        <div className="gw-current card-raised">
          <div className="gw-current-top">
            <div>
              <div className="gw-current-title">{giveaway.title}</div>
              {isActive && (
                <p className="gw-current-desc">{giveaway.description}</p>
              )}
              {isActive && (data.entries?.length ?? 0) > 0 && (
                <div className="gw-entry-count">
                  <span className="gw-entry-count-num">
                    {data.entries.length}
                  </span>
                  <span className="gw-entry-count-lbl">people entered</span>
                </div>
              )}

              {!isPlaytime && giveaway.max_spots && (
                <div className="gw-entry-count-lbl">
                  <span className="gw-entry-count-num">{giveaway.max_spots}</span> maximum spots per player
                </div>
              )}
              {isPlaytime && giveaway.min_playtime_seconds != null && (
                <div className="gw-entry-count-lbl">
                  Min. <span className="gw-entry-count-num">{Math.round(giveaway.min_playtime_seconds / 60)} min</span> playtime required · equal chance for all
                </div>
              )}
            </div>

            {isActive && (
              <div className="gw-prizes">
                {giveaway.prizes.map((p, i) => {
                  const labels = {
                    1: "1st Place",
                    2: "2nd Place",
                    3: "3rd Place",
                  };
                  return (
                    <div key={i} className="gw-prize-row">
                      <div>
                        <div className="gw-prize-label">
                          {labels[p.place] ?? `${p.place}th Place`}
                        </div>
                        <div className="gw-prize-name">{p.prize}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isActive && <HowToEnter giveaway={giveaway} />}
          </div>

          {isActive && timeLeft && (
            <div className="gw-countdown">
              <div className="gw-cd-label-top">Spin happens in</div>
              <div className="gw-cd-blocks">
                <CountdownBlock label="Days" value={timeLeft.d} />
                <CountdownBlock label="Hours" value={timeLeft.h} />
                <CountdownBlock label="Minutes" value={timeLeft.m} />
                <CountdownBlock
                  key={timeLeft.s}
                  label="Seconds"
                  value={timeLeft.s}
                />
              </div>
            </div>
          )}

          {isActive && data.entries?.length > 0 && (
            <div className="gw-participants">
              <div className="gw-participants-label">Who's in</div>
              <div className="gw-participants-row">
                {data.entries.slice(0, 8).map((e, i) => (
                  <img
                    key={i}
                    className="gw-participant-avatar"
                    src={`https://mc-heads.net/avatar/${entryName(e)}/32`}
                    alt={entryName(e)}
                    title={entryName(e)}
                  />
                ))}
                {data.entries.length > 8 && (
                  <div className="gw-participants-more">
                    +{data.entries.length - 8}
                  </div>
                )}
              </div>
              <button
                className="gw-view-entries-btn-current"
                onClick={() =>
                  setEntriesModal({
                    title: giveaway.title,
                    entries: data.entries,
                    isPlaytime,
                  })
                }
              >
                View all entries ({data.entries.length})
              </button>
            </div>
          )}
        </div>

        {awaitingWinners && (
          <div className="gw-countdown">
            <div className="gw-cd-label-top">Picking winners…</div>
            <div className="gw-picking-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {showSpin && (
          <SpinSection
            winners={frozenWinners}
            allDiscordTags={allDiscordTags}
            giveaway={giveaway}
          />
        )}
        {showSummary && <WinnersSummary winners={data.winners} />}

        {past.length > 0 && (
          <>
            <div className="section-label" style={{ marginTop: "2.5rem" }}>
              <span className="sl-text">Previous Giveaways</span>
            </div>
            <div className="grid-auto gw-past-grid">
              {past.map((g) => (
                <PastCard
                  key={g.id}
                  giveaway={g}
                  onViewEntries={() =>
                    setEntriesModal({
                      title: g.title,
                      entries: g.entries ?? [],
                      isPlaytime: false,
                    })
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>

      {entriesModal && (
        <EntriesModal
          title={entriesModal.title}
          entries={entriesModal.entries}
          isPlaytime={entriesModal.isPlaytime ?? false}
          onClose={() => setEntriesModal(null)}
        />
      )}
    </>
  );
}
